import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { getFirestore, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import {
  FiBriefcase, FiCheckCircle, FiAlertTriangle, FiArchive, FiRotateCcw, FiEdit, FiSearch,
  FiMapPin, FiPhone, FiMail, FiClock, FiImage, FiChevronDown, FiChevronUp, FiFileText, FiX, FiUser, FiDownload
} from 'react-icons/fi';
import { exportBusinessesCsv, setupProgress, userName } from './admin/adminUtils';
import './AdminBusinesses.css';
import AdminAddBusiness from './admin/AdminAddBusiness';

const FILTERS = ['All', 'Unverified', 'Setup incomplete', 'Verified', 'Archived'];

export default function AdminBusinesses() {
  const { businesses, setBusinesses, users, usersById, setError } = useOutletContext();
  const [adding, setAdding] = useState(false);
  const db = getFirestore();
  const [filter, setFilter] = useState('Unverified');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [confirmVerify, setConfirmVerify] = useState(null); // business object pending confirmation
  const [toast, setToast] = useState(null); // { message } or null

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const setVerified = async (businessId, isVerified) => {
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        isVerified, updatedAt: serverTimestamp(),
      });
      setBusinesses(prev => prev.map(b => (b.id === businessId ? { ...b, isVerified } : b)));
      return true;
    } catch (err) {
      console.error('Error updating verification:', err);
      setError(`Couldn't update verification: ${err.code || err.message}`);
      return false;
    }
  };

  const setArchived = async (businessId, isArchived) => {
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        isArchived, updatedAt: serverTimestamp(),
      });
      setBusinesses(prev => prev.map(b => (b.id === businessId ? { ...b, isArchived } : b)));
    } catch (err) {
      console.error('Error updating archive status:', err);
      setError(`Couldn't update archive status: ${err.code || err.message}`);
    }
  };

  const handleVerifyClick = (business) => {
    setConfirmVerify(business);
  };

  const confirmVerifyBusiness = async () => {
    if (!confirmVerify) return;
    const business = confirmVerify;
    setConfirmVerify(null);
    const success = await setVerified(business.id, true);
    if (success) {
      setToast(`${business.businessName} has been verified.`);
    }
  };

  const handleUnverify = async (business) => {
    const success = await setVerified(business.id, false);
    if (success) {
      setToast(`${business.businessName} is now unverified.`);
    }
  };

  const toggleExpand = (id) => setExpandedId(prev => (prev === id ? null : id));

  const formatDate = (ts) => {
    if (!ts) return 'Unknown';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString();
  };

  const q = query.trim().toLowerCase();
  const visible = businesses.filter(b => {
    if (filter === 'Unverified') return !b.isVerified && !b.isArchived;
    if (filter === 'Setup incomplete') return !b.isArchived && !setupProgress(b, usersById[b.ownerId]).complete;
    if (filter === 'Verified') return b.isVerified && !b.isArchived;
    if (filter === 'Archived') return b.isArchived;
    return true;
  }).filter(b => !q || `${b.businessName} ${b.category} ${b.email} ${usersById[b.ownerId]?.email || ''}`.toLowerCase().includes(q));

  return (
    <div className="admin-section">
        {adding && <AdminAddBusiness users={users} onCancel={() => setAdding(false)} onSaved={business => {
          setBusinesses(prev => [business, ...prev.filter(b => b.id !== business.id)]);
          setFilter('Unverified');
          setQuery('');
          setExpandedId(business.id);
          setAdding(false);
          setToast(`${business.businessName} created for the selected user.`);
        }} />}
        <div className="admin-toolbar">
          <button type="button" className="btn-primary" disabled={adding} onClick={() => setAdding(true)}>Add Business</button>
          <div className="admin-filters">
            {FILTERS.map(f => (
              <button
                key={f}
                className={`filter-btn ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
          <label className="admin-search">
            <FiSearch aria-hidden="true" />
            <input type="search" placeholder="Search business, category or email" value={query} onChange={e => setQuery(e.target.value)} />
          </label>
          <button type="button" className="admin-logout-button" onClick={() => exportBusinessesCsv(visible, usersById)} disabled={visible.length === 0}>
            <FiDownload aria-hidden="true" /> <span>Export CSV</span>
          </button>
        </div>

        {visible.length === 0 && (
          <div className="empty-state">
            <FiBriefcase size={40} />
            <p>No businesses in this view.</p>
          </div>
        )}

        <div className="admin-business-list">
          {visible.map(business => {
            const owner = usersById[business.ownerId];
            const progress = setupProgress(business, owner);
            return (
            <div key={business.id} className="admin-business-row">
              <div className="admin-business-summary" onClick={() => toggleExpand(business.id)}>
                <div className="admin-business-identity">
                  {business.profileImageUrl ? (
                    <img src={business.profileImageUrl} alt={business.businessName} className="business-thumb" />
                  ) : (
                    <div className="business-thumb-placeholder"><FiBriefcase /></div>
                  )}
                  <div>
                    <h3>{business.businessName}</h3>
                    <span className="admin-business-meta">
                      {business.category} · created {formatDate(business.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="admin-business-summary-right">
                  <div className="admin-business-badges">
                    <span className={`admin-setup-progress ${progress.complete ? 'complete' : ''}`}>
                      Setup {progress.done}/{progress.total}
                    </span>
                    {business.isVerified ? (
                      <span className="badge-verified"><FiCheckCircle /> Verified</span>
                    ) : (
                      <span className="badge-unverified"><FiAlertTriangle /> Unverified</span>
                    )}
                    {business.isArchived && <span className="badge-archived">Archived</span>}
                  </div>
                  <span className="expand-chevron">
                    {expandedId === business.id ? <FiChevronUp /> : <FiChevronDown />}
                  </span>
                </div>
              </div>

              {expandedId === business.id && (
                <div className="admin-business-details">
                  {!progress.complete && (
                    <ul className="admin-setup-checklist" aria-label="Setup checklist">
                      {progress.items.map(item => (
                        <li key={item.label} className={item.done ? '' : 'missing'}>
                          {item.done ? <FiCheckCircle /> : <FiAlertTriangle />} {item.label}
                        </li>
                      ))}
                    </ul>
                  )}
                  {business.description && (
                    <p className="admin-detail-description">{business.description}</p>
                  )}
                  <div className="admin-detail-grid">
                    {business.address && (
                      <div className="admin-detail-item"><FiMapPin /> {business.address}</div>
                    )}
                    {business.phone && (
                      <div className="admin-detail-item"><FiPhone /> {business.phone}</div>
                    )}
                    {business.email && (
                      <a href={`mailto:${business.email}`} className="admin-detail-item admin-detail-link" onClick={(e) => e.stopPropagation()}>
                        <FiMail /> {business.email}
                      </a>
                    )}
                    {business.businessHours && (
                      <div className="admin-detail-item"><FiClock /> {business.businessHours}</div>
                    )}
                    {business.additionalImageUrls?.length > 0 && (
                      <div className="admin-detail-item">
                        <FiImage /> {business.additionalImageUrls.length} gallery image{business.additionalImageUrls.length === 1 ? '' : 's'}
                      </div>
                    )}
                    <div className="admin-detail-item admin-detail-owner-id">
                      <FiUser /> Owner: {owner ? `${userName(owner)} (${owner.email || 'no email'})` : business.ownerId}
                    </div>
                  </div>

                  {business.additionalImageUrls?.length > 0 && (
                    <div className="admin-gallery-row">
                      {business.additionalImageUrls.map((url, i) => (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <img src={url} alt={`Gallery ${i + 1}`} className="admin-gallery-thumb" />
                        </a>
                      ))}
                    </div>
                  )}

                  {business.companyDocumentUrls?.length > 0 && (
                    <div className="admin-documents-row">
                      {business.companyDocumentUrls.map((url, i) => (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-document-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FiFileText /> Document {i + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="admin-business-actions">
                <Link to={`/edit-business/${business.id}`} className="icon-text-btn">
                  <FiEdit /> Edit profile
                </Link>
                {business.isVerified ? (
                  <button className="icon-text-btn" onClick={() => handleUnverify(business)}>
                    Unverify
                  </button>
                ) : (
                  <button className="icon-text-btn primary" onClick={() => handleVerifyClick(business)}>
                    <FiCheckCircle /> Verify
                  </button>
                )}
                {business.isArchived ? (
                  <button className="icon-text-btn" onClick={() => setArchived(business.id, false)}>
                    <FiRotateCcw /> Unarchive
                  </button>
                ) : (
                  <button className="icon-text-btn danger" onClick={() => setArchived(business.id, true)}>
                    <FiArchive /> Archive
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>

      {confirmVerify && (
        <div className="admin-modal-overlay" onClick={() => setConfirmVerify(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <button className="admin-modal-close" onClick={() => setConfirmVerify(null)}>
              <FiX />
            </button>
            <div className="admin-modal-icon">
              <FiCheckCircle />
            </div>
            <h2>Verify this business?</h2>
            <p>
              You're about to mark <strong>{confirmVerify.businessName}</strong> as verified.
              It will become visible to customers once verified.
            </p>
            <div className="admin-modal-actions">
              <button className="btn-secondary" onClick={() => setConfirmVerify(null)}>Cancel</button>
              <button className="btn-primary" onClick={confirmVerifyBusiness}>
                <FiCheckCircle /> Yes, Verify
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="admin-toast">
          <FiCheckCircle />
          <span>{toast}</span>
        </div>
      )}
    </div>
  );
}
